import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { Label } from "./ui/label";

/**
 * MK5 (GO Frank 3 november 2026): modelprofielen per rol, in Instellingen > Model.
 *
 * Vier rollen: chat (het model hierboven), code ("Model voor code" hieronder, LC1), onderzoek en agents. Onderzoek en
 * agents kiezen hier een lokaal model; leeg is hetzelfde als de chat. Een agent met een eigen model op zijn kaart houdt
 * dat. Per rol een terugvalmodel, ALLEEN LOKAAL (besluit Frank 3 nov): bij een time-out of een model dat niet bestaat
 * antwoordt dat model een keer, en het antwoord zegt het. De lijst is wat er op deze Mac staat (MK1, de catalogus);
 * een embeddingmodel staat er niet in, want dat kan geen antwoord schrijven.
 */

export const ROLLEN = [
  { rol: "chat", naam: "Chat", eigen: false, uitleg: "Het model hierboven." },
  { rol: "code", naam: "Code", eigen: false, uitleg: "Model voor code, hieronder." },
  { rol: "onderzoek", naam: "Onderzoek", eigen: true, uitleg: "Een onderzoeksvraag op het web." },
  { rol: "agents", naam: "Agents", eigen: true, uitleg: "Een agent zonder eigen model op zijn kaart." },
];

export const LEEG = Object.freeze({ onderzoek: "", agents: "", terugval: { chat: "", code: "", onderzoek: "", agents: "" } });

/** De lokale modellen die een antwoord kunnen schrijven, uit de catalogus. PUUR. */
export function tekstModellen(lokaal = []) {
  return lokaal.filter((m) => (m.mogelijkheden || []).includes("completion")).map((m) => m.naam);
}

/** De rollen in hun volle vorm, ook als de server (nog) niets gaf. PUUR. */
export function vorm(rollen) {
  return { ...LEEG, ...(rollen || {}), terugval: { ...LEEG.terugval, ...(rollen?.terugval || {}) } };
}

/** De rollen met een veld veranderd; `terugval` per rol. PUUR. */
export function metWijziging(rollen, rol, veld, waarde) {
  const basis = vorm(rollen);
  if (veld === "terugval") return { ...basis, terugval: { ...basis.terugval, [rol]: waarde } };
  return { ...basis, [rol]: waarde };
}

function Keuze({ id, waarde, modellen, leegTekst, onChange, t }) {
  // Een bewaarde keuze die niet meer op deze Mac staat, blijft zichtbaar in plaats van stil te verdwijnen.
  const opties = waarde && !modellen.includes(waarde) ? [waarde, ...modellen] : modellen;
  return (
    <select id={id} data-testid={id} value={waarde || ""} onChange={(e) => onChange(e.target.value)}
            className="h-8 w-full rounded-md border bg-background px-2 text-xs">
      <option value="">{t(leegTekst)}</option>
      {opties.map((m) => <option key={m} value={m}>{m}</option>)}
    </select>
  );
}

export default function ModelRollen() {
  const { t } = useTaal();
  const [rollen, setRollen] = useState(LEEG);
  const [modellen, setModellen] = useState([]);

  useEffect(() => {
    let weg = false;
    Promise.all([axios.get(`${API}/settings`), axios.get(`${API}/ollama/catalogus`)])
      .then(([s, c]) => {
        if (weg) return;
        setRollen(vorm(s.data.model_rollen));
        setModellen(tekstModellen(c.data.lokaal));
      })
      .catch(() => {});
    return () => { weg = true; };
  }, []);

  const zet = useCallback(async (rol, veld, waarde) => {
    const nieuw = metWijziging(rollen, rol, veld, waarde);
    try {
      const { data } = await axios.put(`${API}/settings`, { model_rollen: nieuw });
      setRollen(data.model_rollen || nieuw);
      toast.success(t("Opgeslagen"));
    } catch (e) {
      // De server weigert een niet-lokaal model; dan blijft de keuze wat ze was, met de zin van de server.
      toast.error(e?.response?.data?.detail || t("Niet opgeslagen"));
    }
  }, [rollen, t]);

  return (
    <div className="space-y-2" data-testid="model-rollen">
      <Label>{t("Model per rol")}</Label>
      <p className="text-xs text-muted-foreground">
        {t("Een terugval gaat alleen van lokaal naar lokaal: bij een time-out of een model dat niet bestaat antwoordt het terugvalmodel, en het antwoord zegt dat.")}
      </p>
      {ROLLEN.map(({ rol, naam, eigen, uitleg }) => (
        <div key={rol} className="grid grid-cols-[5.5rem_1fr_1fr] items-center gap-2" data-testid={`rol-${rol}`}>
          <span className="text-sm" title={t(uitleg)}>{t(naam)}</span>
          {eigen ? (
            <Keuze id={`rol-model-${rol}`} waarde={rollen[rol]} modellen={modellen} leegTekst="Zelfde als de chat"
                   onChange={(w) => zet(rol, rol, w)} t={t} />
          ) : (
            <span className="text-xs text-muted-foreground">{t(uitleg)}</span>
          )}
          <Keuze id={`rol-terugval-${rol}`} waarde={(rollen.terugval || {})[rol]} modellen={modellen}
                 leegTekst="Geen terugval" onChange={(w) => zet(rol, "terugval", w)} t={t} />
        </div>
      ))}
    </div>
  );
}
